<template>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div :lang="contentLanguage" dusk="SmartZoomArticleContent" class="smart-zoom-content pb-4" v-html="content"></div>
</template>

<script>
'use strict';

export default {
    props: {
        content: {
            type: String,
            default: '',
        },
        contentLanguage: {
            type: String,
            default: '',
        },
    },
    mounted() {
        this.addTargetBlankToHyperlinks();
    },
    methods: {
        addTargetBlankToHyperlinks() {
            // We add `target=_blank` to all links because otherwise links want opening in the iframe and broken this.
            const links = this.$el.querySelectorAll('a');
            links.forEach(link => link.setAttribute('target', '_blank'));
        },
    },
};
</script>

<style scoped>
/* Base content styles */
.smart-zoom-content {
    @apply leading-relaxed text-theme-text;
    font-size: 1em;
}

/* Typography sizing that scales with zoom */
.smart-zoom-content :deep(h1) {
    @apply mb-4 mt-6 font-semibold tracking-tight;
    font-size: 2.25em;
}

.smart-zoom-content :deep(h2) {
    @apply mb-3 mt-5 font-semibold tracking-tight;
    font-size: 1.875em;
}

.smart-zoom-content :deep(h3) {
    @apply mb-2 mt-4 font-semibold tracking-tight;
    font-size: 1.5em;
}

.smart-zoom-content :deep(h4) {
    @apply mb-2 mt-3 font-semibold tracking-tight;
    font-size: 1.25em;
}

.smart-zoom-content :deep(h5) {
    @apply mb-2 mt-3 font-semibold tracking-tight;
    font-size: 1.125em;
}

.smart-zoom-content :deep(h6) {
    @apply mb-2 mt-3 font-semibold tracking-tight;
    font-size: 1em;
}

.smart-zoom-content :deep(p) {
    @apply mb-4 tracking-tight;
    font-size: 1em;
}

.smart-zoom-content :deep(ul),
.smart-zoom-content :deep(ol) {
    @apply mb-4 pl-6;
}

.smart-zoom-content :deep(li) {
    @apply mb-2 tracking-tight;
    font-size: 1em;
}

.smart-zoom-content :deep(blockquote) {
    @apply my-4 border-l-4 border-theme-border pl-4 italic;
    font-size: 1em;
}

.smart-zoom-content :deep(code) {
    @apply rounded bg-theme-icon-bg px-1 py-0.5 text-sm;
    font-size: 0.875em;
}

.smart-zoom-content :deep(pre) {
    @apply my-4 overflow-x-auto rounded bg-theme-icon-bg p-4;
    font-size: 0.875em;
}

.smart-zoom-content :deep(a) {
    @apply text-blue-600 hover:underline dark:text-blue-400;
}

.smart-zoom-content :deep(strong) {
    @apply font-semibold;
}

.smart-zoom-content :deep(em) {
    @apply italic;
}

.smart-zoom-content :deep(hr) {
    @apply my-6 border-theme-border;
}

/* Custom styles for content elements */
:deep(aside) {
    @apply mx-0 my-3 rounded border border-theme-border bg-theme-icon-bg p-3;
}

:deep(iframe) {
    @apply max-h-full max-w-full;
}

/* Attachment styles */
:deep(.attachment) {
    @apply relative m-0 inline-block max-w-full p-0;
}

:deep(.attachment a) {
    @apply text-inherit no-underline hover:text-inherit;
}

:deep(.attachment__caption) {
    @apply p-0 text-center;
}

:deep(.attachment__caption .attachment__name + .attachment__size::before) {
    content: ' · ';
}

:deep(.attachment--preview) {
    @apply w-full text-center;
}

:deep(.attachment--preview .attachment__caption) {
    @apply text-sm leading-tight text-theme-toolbar-icon;
}

:deep(.attachment--file) {
    @apply mx-0.5 mb-0.5 rounded border border-theme-border px-4 py-1.5 leading-none text-theme-text;
}

/* Gallery styles */
:deep(.attachment-gallery) {
    @apply relative m-0 flex flex-wrap p-0;
}

:deep(.attachment-gallery .attachment) {
    @apply max-w-[33.333%] flex-1 basis-1/3 px-2;
}

:deep(.attachment-gallery.attachment-gallery--2 .attachment),
:deep(.attachment-gallery.attachment-gallery--4 .attachment) {
    @apply max-w-[50%] basis-1/2;
}

/* Font size transition for all elements */
:deep(*) {
    @apply transition-[font-size] duration-100 ease-in;
}
</style>
